import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { catchError, debounceTime, map, of, Subscription, switchMap, tap } from 'rxjs';
import { CollegeItem, InstitutionService } from '@edtech-ui/core/services/institution.service';

/**
 * Searchable college/university picker shared by the registration flows and
 * the profile page. The host projects its own <input> (bound to `control`) so
 * each screen keeps its input styling and validation messages; this component
 * owns the search, the results panel and the "Can't find" link.
 *
 * Selection state stays with the host: listen to `collegeSelected` and
 * `selectionCleared` to track the chosen college id. Hosts that set the
 * control value programmatically must use `{ emitEvent: false }`, otherwise it
 * is treated as typing and clears the selection.
 *
 * Theme the panel and link via the `--college-ac-*` CSS custom properties.
 */
@Component({
	selector: 'app-college-autocomplete',
	standalone: true,
	imports: [CommonModule],
	templateUrl: './college-autocomplete.component.html',
	styleUrl: './college-autocomplete.component.scss'
})
export class CollegeAutocompleteComponent implements OnInit, OnDestroy {
	/** The form control bound to the projected input. */
	@Input({ required: true }) control!: AbstractControl<string | null>;
	/** Characters required before searching. */
	@Input() minSearchLength = 5;
	@Input() showCantFind = true;

	@Output() collegeSelected = new EventEmitter<CollegeItem>();
	/** Emitted on every user edit, since the typed text no longer matches a selected college. */
	@Output() selectionCleared = new EventEmitter<void>();
	@Output() cantFind = new EventEmitter<void>();

	results: CollegeItem[] = [];
	isSearching = false;
	isOpen = false;

	private subscription = new Subscription();

	constructor(
		private _institutionService: InstitutionService,
		private _elementRef: ElementRef<HTMLElement>
	) {}

	ngOnInit(): void {
		this.subscription.add(
			this.control.valueChanges.pipe(
				// Clear immediately; waiting for the debounce would let a submit go
				// through with an id that no longer matches the typed name.
				tap(() => this.selectionCleared.emit()),
				debounceTime(350),
				map(value => (value || '').trim()),
				tap(term => {
					const searchable = term.length >= this.minSearchLength;
					this.isOpen = searchable;
					this.isSearching = searchable;
				}),
				switchMap(term => term.length < this.minSearchLength
					? of<CollegeItem[]>([])
					: this._institutionService.searchInstitutions(term).pipe(catchError(() => of<CollegeItem[]>([])))
				)
			).subscribe(items => {
				this.results = items;
				this.isSearching = false;
			})
		);
	}

	select(college: CollegeItem): void {
		this.control.setValue(college.name, { emitEvent: false });
		this.control.markAsDirty();
		this.close();
		this.collegeSelected.emit(college);
	}

	onCantFind(): void {
		this.close();
		this.cantFind.emit();
	}

	/** Close when focus leaves the component (input, panel and link all count as inside). */
	onFocusOut(event: FocusEvent): void {
		const next = event.relatedTarget as Node | null;
		if (!next || !this._elementRef.nativeElement.contains(next)) {
			this.close();
		}
	}

	close(): void {
		this.isOpen = false;
		this.isSearching = false;
		this.results = [];
	}

	trackById(_: number, college: CollegeItem): CollegeItem['id'] {
		return college.id;
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}
